/**
 * Tuning-profile persistence.
 *
 * ## Where this lives now
 *
 * In the main `harmony-companion` database, as of its version 2. It used to be a second
 * database of its own — `hc-tuning` — because the sketchbook schema was being written in
 * parallel and two modules calling `openDB` on one name with different `upgrade`
 * callbacks is a `VersionError` waiting to happen. That reason expired; the stores now sit
 * beside the sketchbook's, and `sketchbook/db.ts` owns the whole schema.
 *
 * The shape is unchanged, because it had to be: `tuningProfiles` (keyPath `id`) holds
 * `TuningProfile` records, and `tuningMeta` (keyPath `key`) is a plain key/value store of
 * `{ key, value }` holding `'active'` (the id of the profile the session screen should
 * load) and `'matrix'` (the in-progress test-matrix run, so a reload on the iPad does not
 * lose an hour of testing). Anybody who had saved a profile in `hc-tuning` keeps it:
 * `adoptLegacyTuningDatabase` in `sketchbook/db.ts` copies the records across on the
 * upgrade and then deletes the old database.
 *
 * Nothing here reaches for storage directly. Every read and write still goes through
 * `withStore`, which is now the seam onto the shared `StorageDriver`.
 *
 * ## Why it never throws
 *
 * `loadActiveParams()` is on the session's startup path. A private-browsing Safari
 * with IndexedDB disabled, a corrupt database, a quota error — none of those are
 * reasons for the app not to listen to a piano. Every failure falls back to an
 * in-memory store and the defaults, and `getStorageMode()` reports which happened so
 * the tuning screen can say "not persisting" out loud rather than silently losing the
 * user's work.
 *
 * Stored values are always passed through `mergeRecognizerParams`, so a profile saved
 * before a parameter's range changed can never put the DSP into an impossible state.
 */

import {
  DEFAULT_RECOGNIZER_PARAMS,
  mergeRecognizerParams,
  RECOGNIZER_PARAM_DESCRIPTORS,
  type RecognizerParams,
} from '@hc/shared';
import { getDb, storageStatus, type StorageDriver } from '../sketchbook/db.js';
import {
  DEFAULT_ENGINE_ID,
  isRecognizerEngineId,
  type RecognizerEngineId,
} from '../recognition/engines/types.js';

/**
 * The database these stores used to live in, and the version it reached.
 *
 * Still exported because it is the name the one-time migration looks for; nothing opens
 * it any more. The live schema is `DB_NAME` / `DB_VERSION` in `sketchbook/db.ts`.
 */
export const TUNING_DB_NAME = 'hc-tuning';
export const TUNING_DB_VERSION = 1;
export const PROFILE_STORE = 'tuningProfiles';
export const META_STORE = 'tuningMeta';
export const ACTIVE_META_KEY = 'active';

/** The id of the profile that exists from the first run and cannot be deleted. */
export const DEFAULT_PROFILE_ID = 'default';

/**
 * Which recognition engine is being heard, and which others are being measured beside it.
 *
 * It lives on the profile rather than in its own meta key because it belongs with the
 * parameters: "this piano, this room, these numbers, and this engine" is one setting, and
 * a comparison run recorded against the wrong engine is worthless. Optional so that a
 * profile saved before engines existed loads unchanged and means "the custom engine",
 * which is what it was.
 */
export interface EngineSelection {
  /** The engine whose events reach the session. */
  primary: RecognizerEngineId;
  /** Engines running on the same audio for comparison. Never contains `primary`. */
  alsoRun: RecognizerEngineId[];
}

export const DEFAULT_ENGINE_SELECTION: Readonly<EngineSelection> = Object.freeze({
  primary: DEFAULT_ENGINE_ID,
  alsoRun: Object.freeze([] as RecognizerEngineId[]) as RecognizerEngineId[],
});

export interface TuningProfile {
  /** Stable slug. `'default'` always exists. */
  id: string;
  name: string;
  params: RecognizerParams;
  createdAt: number;
  updatedAt: number;
  /** Free text: which piano, which room, which microphone position. */
  notes?: string;
  /** Absent means the default: the custom engine alone. */
  engines?: EngineSelection;
}

/** Coerce stored or imported engine state into something selectable. Never throws. */
export function sanitizeEngineSelection(input: unknown): EngineSelection {
  if (typeof input !== 'object' || input === null) return { ...DEFAULT_ENGINE_SELECTION };
  const raw = input as Record<string, unknown>;
  const primary = isRecognizerEngineId(raw.primary) ? raw.primary : DEFAULT_ENGINE_ID;
  const alsoRun: RecognizerEngineId[] = [];
  if (Array.isArray(raw.alsoRun)) {
    for (const value of raw.alsoRun) {
      if (!isRecognizerEngineId(value) || value === primary || alsoRun.includes(value)) continue;
      alsoRun.push(value);
    }
  }
  return { primary, alsoRun };
}

/** `tuningMeta` is a plain key/value store so new tuning-screen state needs no migration. */
interface MetaRecord {
  key: string;
  value: unknown;
}

export type StorageMode = 'indexeddb' | 'memory';

let storageMode: StorageMode = 'indexeddb';

/** Fallback store, used when IndexedDB is unavailable. Same shape, no durability. */
const memoryProfiles = new Map<string, TuningProfile>();
const memoryMeta = new Map<string, unknown>();

export function getStorageMode(): StorageMode {
  return storageMode;
}

function indexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

/**
 * The single choke point for database access.
 *
 * `getDb()` from the sketchbook never rejects and never returns null — when storage is
 * unusable it hands back the in-memory driver and flips `storageStatus().durable`. A
 * driver that is not durable is exactly what "no database" used to mean here, so the maps
 * above stay the fallback rather than routing through a second in-memory store: the tuning
 * screen's own `getStorageMode()` keeps saying the same thing it always did.
 */
async function withStore<T>(fn: (db: StorageDriver) => Promise<T>, fallback: T): Promise<T> {
  const db = await getDb();
  const durable = db.durable && storageStatus().durable;
  storageMode = durable ? 'indexeddb' : 'memory';
  if (!durable) return fallback;
  try {
    return await fn(db);
  } catch (error) {
    console.warn('[hc] tuning profile storage error; continuing in memory.', error);
    storageMode = 'memory';
    return fallback;
  }
}

function nowMs(): number {
  return Date.now();
}

export function createDefaultProfile(): TuningProfile {
  const at = nowMs();
  return {
    id: DEFAULT_PROFILE_ID,
    name: 'Default',
    params: {
      ...DEFAULT_RECOGNIZER_PARAMS,
      harmonicWeights: [...DEFAULT_RECOGNIZER_PARAMS.harmonicWeights],
    },
    createdAt: at,
    updatedAt: at,
  };
}

/**
 * Coerce anything — a stored record, a pasted JSON file — into a valid profile.
 * Unknown keys are dropped; out-of-range values are clamped; missing values take the
 * default. Returns `null` only when the input is not an object at all.
 */
export function sanitizeProfile(
  input: unknown,
  fallbackId = DEFAULT_PROFILE_ID,
): TuningProfile | null {
  if (typeof input !== 'object' || input === null) return null;
  const raw = input as Record<string, unknown>;
  const rawParams = (
    typeof raw.params === 'object' && raw.params !== null ? raw.params : {}
  ) as Record<string, unknown>;

  const patch: Partial<RecognizerParams> = {};
  for (const descriptor of RECOGNIZER_PARAM_DESCRIPTORS) {
    const value = rawParams[descriptor.key];
    if (descriptor.kind === 'number-array') {
      if (Array.isArray(value) && value.every((entry) => typeof entry === 'number')) {
        (patch[descriptor.key] as number[]) = value as number[];
      }
      continue;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      (patch[descriptor.key] as number) = value;
    }
  }

  const at = nowMs();
  const profile: TuningProfile = {
    id: typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : fallbackId,
    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : 'Imported profile',
    params: mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, patch),
    createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : at,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : at,
  };
  if (typeof raw.notes === 'string' && raw.notes.trim()) profile.notes = raw.notes;
  // Only written when it was actually stored, so `params` equality against the defaults —
  // which several tests and the import path rely on — is unaffected by engines existing.
  if (raw.engines !== undefined) profile.engines = sanitizeEngineSelection(raw.engines);
  return profile;
}

/** The active profile's engine selection, or the default. Never rejects. */
export async function loadActiveEngines(): Promise<EngineSelection> {
  try {
    return sanitizeEngineSelection((await getActiveProfile()).engines);
  } catch {
    return { ...DEFAULT_ENGINE_SELECTION };
  }
}

/** Write the engine selection into the active profile, leaving its parameters alone. */
export async function saveActiveEngines(engines: EngineSelection): Promise<TuningProfile> {
  const active = await getActiveProfile();
  return saveProfile({ ...active, engines: sanitizeEngineSelection(engines) });
}

export async function listProfiles(): Promise<TuningProfile[]> {
  const stored = await withStore(
    async (db) => (await db.getAll(PROFILE_STORE)) as TuningProfile[],
    [...memoryProfiles.values()],
  );
  const profiles = stored.length ? stored : [];
  if (!profiles.some((profile) => profile.id === DEFAULT_PROFILE_ID)) {
    profiles.unshift(memoryProfiles.get(DEFAULT_PROFILE_ID) ?? createDefaultProfile());
  }
  return profiles.sort((a, b) => a.name.localeCompare(b.name));
}

export async function getProfile(id: string): Promise<TuningProfile | null> {
  const stored = await withStore(
    async (db) => ((await db.get(PROFILE_STORE, id)) ?? null) as TuningProfile | null,
    memoryProfiles.get(id) ?? null,
  );
  if (stored) return sanitizeProfile(stored, id);
  return id === DEFAULT_PROFILE_ID ? createDefaultProfile() : null;
}

export async function saveProfile(profile: TuningProfile): Promise<TuningProfile> {
  const next: TuningProfile = { ...profile, updatedAt: nowMs() };
  memoryProfiles.set(next.id, next);
  await withStore(async (db) => {
    await db.put(PROFILE_STORE, next);
    return true;
  }, true);
  return next;
}

export async function deleteProfile(id: string): Promise<void> {
  if (id === DEFAULT_PROFILE_ID) return; // the default is a floor, not a row
  memoryProfiles.delete(id);
  await withStore(async (db) => {
    await db.delete(PROFILE_STORE, id);
    return true;
  }, true);
  if ((await getActiveProfileId()) === id) await setActiveProfileId(DEFAULT_PROFILE_ID);
}

/**
 * Generic key/value access to the `tuningMeta` store. Used for the active profile id
 * and for the in-progress test-matrix run; anything else the tuning screen needs to
 * remember belongs here too, rather than in a new store.
 */
export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const inMemory = memoryMeta.has(key) ? (memoryMeta.get(key) as T) : fallback;
  return withStore(async (db) => {
    const record = (await db.get(META_STORE, key)) as MetaRecord | undefined;
    return record === undefined ? fallback : (record.value as T);
  }, inMemory);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  memoryMeta.set(key, value);
  await withStore(async (db) => {
    await db.put(META_STORE, { key, value } satisfies MetaRecord);
    return true;
  }, true);
}

export async function getActiveProfileId(): Promise<string> {
  return getMeta<string>(ACTIVE_META_KEY, DEFAULT_PROFILE_ID);
}

export async function setActiveProfileId(id: string): Promise<void> {
  await setMeta(ACTIVE_META_KEY, id);
}

export async function getActiveProfile(): Promise<TuningProfile> {
  const id = await getActiveProfileId();
  return (await getProfile(id)) ?? createDefaultProfile();
}

/**
 * **The export the session screen calls at startup.**
 *
 * Returns the active profile's parameters, clamped to the descriptor ranges, or the
 * defaults if anything at all went wrong. Never rejects. This is the mechanism that
 * makes tuning on the iPad survive without a rebuild — the entire point of the chunk.
 *
 *     const params = await loadActiveParams();
 *     recognizer.setParams(params);
 */
export async function loadActiveParams(): Promise<RecognizerParams> {
  try {
    const profile = await getActiveProfile();
    return mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, profile.params);
  } catch {
    return {
      ...DEFAULT_RECOGNIZER_PARAMS,
      harmonicWeights: [...DEFAULT_RECOGNIZER_PARAMS.harmonicWeights],
    };
  }
}

/**
 * Write parameters into the active profile. Called on every slider change from the
 * tuning screen, so callers should debounce; this does not, because a caller that
 * wants a write to land before a reload needs it not to.
 */
export async function saveActiveParams(params: RecognizerParams): Promise<TuningProfile> {
  const active = await getActiveProfile();
  return saveProfile({
    ...active,
    params: mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, params),
  });
}

/** Reset module state. Tests only. The shared database is reset with `setDb(null)`. */
export function __resetProfileStoreForTests(): void {
  storageMode = indexedDbAvailable() ? 'indexeddb' : 'memory';
  memoryProfiles.clear();
  memoryMeta.clear();
}
